import React, { createContext, useContext, useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { AuthContext } from 'api/context';
import { setTitle } from 'Main/utils';
import Canvas from './canvas';
// import reducer from './reducer';

export const LeadContext = createContext();

const companyRateRequestCache = new Map();
const leadBootstrapTimeoutMs = 10000;

const optionalBootstrapRequest = (request, fallback, label) => {
  let settled = false;
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      console.warn(`Lead bootstrap ${label} timed out; continuing with fallback.`);
      resolve(fallback);
    }, leadBootstrapTimeoutMs);

    Promise.resolve(request)
      .then((data) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(data);
      })
      .catch((error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        console.warn(`Lead bootstrap ${label} failed; continuing with fallback.`, error);
        resolve(fallback);
      });
  });
};

const LeadProvider = ({ children }) => {
  const {
    user, company, newEndpoint, displaySnack, getTaskStatus, AiDesigns,
  } = useContext(AuthContext);

  // set initial state for context provider
  const history = useHistory();
  const { uid, tab } = useParams();
  const [lead, setLead] = useState();

  // lead-related APIs
  const API = newEndpoint('crm/leads/');
  const noteAPI = newEndpoint('crm/notes/');
  const taskAPI = newEndpoint('crm/tasks/');
  const documentAPI = newEndpoint('crm/documents/');
  const productionMapAPI = newEndpoint('crm/production/');
  const packageAPI = newEndpoint('hardware/packages/');
  const hardwareAPI = newEndpoint('hardware/all/');
  const [leadError, setLeadError] = useState(null);
  const proposalAPI = newEndpoint('designer/proposal/');
  const fieldsAPI = newEndpoint('crm/lead/fields/');
  const namevaluesAPI = newEndpoint('crm/lead/namevalues/');
  const pinHouseAPI = newEndpoint('crm/lead/pin_house/');
  const predictAPI = newEndpoint('crm/lead/predict/');
  const leadImagesAPI = newEndpoint('crm/lead/images/');
  const thumbnailsAPI = newEndpoint('crm/lead/thumbnails/');
  const projectAPI = newEndpoint('project_management/projects/');
  const paymentAPI = newEndpoint('project_management/payments/');
  const coverageAPI = newEndpoint('api/coverage/');
  // design tool APIs
  const PVWattsAPI = newEndpoint('designer/pvwatts/');
  const PVWattsHourlyAPI = newEndpoint('designer/pvwatts/hourly/');
  const StringingAPI = newEndpoint('designer/string/auto/');
  const saveShadingsAPI = newEndpoint('designer/gradients/');
  const runShadingsAPI = newEndpoint('designer/gradients/shade');
  const runShadingRequiredClientDataAPI = newEndpoint('designer/gradients/shading_required_client_data');
  const removeShadingsAPI = newEndpoint('designer/gradients/remove/');
  const exportDesignAPI = newEndpoint('designer/export/design/');
  const measureAPI = newEndpoint('designer/measure/');
  const optimizeAPI = newEndpoint('designer/optimize/');
  const edgeDetectionAPI = newEndpoint('designer/edge_detection/');
  const panelPlacementAPI = newEndpoint('designer/panel_placement/');
  const ratesAPI = newEndpoint('hardware/rates/');
  const canvasImportDxfAPI = newEndpoint('designer/import/dxf/');
  // Solar Production Service APIs
  const solarProductionDesignAPI = newEndpoint('designer/solar-production/design/');
  const solarProductionOptimalAPI = newEndpoint('designer/solar-production/optimal/');
  const solarProductionSimulateAPI = newEndpoint('designer/solar-production/simulate/');

  const [stepsDone, setStepsDone] = useState(['Customer Details']);

  // wrapper to facilitate canvas-related operations
  const CanvasAPI = Canvas(newEndpoint('designer/canvas'), uid);

  const pinHouse = (data) => pinHouseAPI.create(data);

  const navigateTab = (newTab) => {
    if (!newTab) return;
    history.push(`/leads/${uid}/${newTab}`);
  };

  const refreshLead = (field, prefetchedLead) => {
    const applyField = (data) => {
      if (!field || !data) return;
      // Functional update so async refreshes (e.g. after Run AI polling) merge
      // into the latest lead state instead of a stale closure — utility and
      // other sections must not be reset when only property is refreshed.
      setLead((prev) => ({ ...prev, [field]: data[field] }));
    };

    if (prefetchedLead) {
      applyField(prefetchedLead);
      return Promise.resolve(prefetchedLead);
    }

    if (field) {
      return API.retrieve(uid).then(applyField);
    }

    return Promise.resolve();
  };

  const setUtility = (utilityOrUpdater) => {
    setLead((prev) => {
      const nextUtility =
        typeof utilityOrUpdater === 'function'
          ? utilityOrUpdater(prev.utility ?? {})
          : utilityOrUpdater;
      return { ...prev, utility: nextUtility };
    });
  };

  const validUtility = (lead?.utility?.usage !== undefined // ?totalUsage
    && lead?.utility?.effectiveRate !== undefined
    && lead?.utility?.fixedCharge !== undefined);

  const updateLead = async (updateData, partial = false) => API.update(uid, updateData, partial).then((data) => {
    let updatedLead;
    setLead((prev) => {
      updatedLead = {
        ...prev,
        ...data,
        utility:
          updateData.utility !== undefined
            ? { ...prev.utility, ...(data.utility ?? {}) }
            : prev.utility,
      };
      return updatedLead;
    });
    return updatedLead;
  });

  const stepsDoneHandler = (newStep) => {
    //STEPS
    //Manual Lead when creating is manually
    //Customer Details, Property Information, Utility Information
    setStepsDone(prevState => [newStep, ...prevState])
  }

  useEffect(() => {
    function fetchData() {
      return Promise.all([
        API.retrieve(uid)
          .then(async (data) => {
            const { utility = {} } = data;
            console.log('Utility in fetchData', utility);
            if (utility?.rate === 'custom') {
              // Preserve existing utility values
              const existingUtility = {
                ...utility,
                provider: 'Custom',
                fixedChargeUnits: '$/month',
                fixedCharge: utility.fixed_charge,
                effectiveRate: utility.rate_amount,
                export_rate: utility.export_rate,
                ufsc: utility.ufsc,
                offset_savings: utility.offset_savings,
                billing_type: utility.billing_type,

                // Initialize net_metering_specs object if billing type is net_metering
                net_metering_specs: utility.billing_type === 'net_metering' ? {
                  ufsc: utility.net_metering_specs?.ufsc || 22,
                  cost_cap: utility.net_metering_specs?.cost_cap || 0,
                  cost_per_kw: utility.net_metering_specs?.cost_per_kw || 0,
                  export_rate: utility.net_metering_specs?.export_rate || utility.rate_amount || 0,
                  system_size: utility.net_metering_specs?.system_size || 0,
                  trueup_month: utility.net_metering_specs?.trueup_month || "April",
                  offset_savings: utility.net_metering_specs?.offset_savings || false,
                  fixed_cost_type: utility.net_metering_specs?.fixed_cost_type || "constant",
                  metering_period: utility.net_metering_specs?.metering_period || "annual"
                } : null,

                // Initialize net_billing_specs object if billing type is net_billing
                net_billing_specs: utility.billing_type === 'net_billing' ? {
                  ufsc: utility.net_billing_specs?.ufsc || 22,
                  cost_cap: utility.net_billing_specs?.cost_cap || 0,
                  cost_per_kw: utility.net_billing_specs?.cost_per_kw || 0,
                  export_rate: utility.net_billing_specs?.export_rate || 0.022, // Default to $0.022/kWh
                  system_size: utility.net_billing_specs?.system_size || 0,
                  fixed_cost_type: utility.net_billing_specs?.fixed_cost_type || "constant",
                  export_percentage: utility.net_billing_specs?.export_percentage || 40 // Default to 40%
                } : null
              };

              if (utility?.company_rate) {
                console.log('Fetching company rate:', utility.company_rate);
                try {
                  const cacheKey = String(utility.company_rate);
                  if (!companyRateRequestCache.has(cacheKey)) {
                    companyRateRequestCache.set(
                      cacheKey,
                      ratesAPI.retrieve(utility.company_rate).catch((error) => {
                        companyRateRequestCache.delete(cacheKey);
                        throw error;
                      })
                    );
                  }
                  const rateData = await companyRateRequestCache.get(cacheKey);
                  console.log('Rate Data from company rate:', rateData);

                  // Handle specs based on billing type
                  if (rateData.billing_type === 'net_metering' && rateData.net_metering_specs) {
                    existingUtility.net_metering_specs = {

                      ...rateData.net_metering_specs
                    };
                    console.log('Updated net_metering_specs from rate data:', existingUtility.net_metering_specs);
                  } else if (rateData.billing_type === 'net_billing' && rateData.net_billing_specs) {
                    existingUtility.net_billing_specs = {

                      ...rateData.net_billing_specs
                    };
                    console.log('Updated net_billing_specs from rate data:', existingUtility.net_billing_specs);
                  }

                  // Update billing type if specified in rate data
                  if (rateData.billing_type) {
                    existingUtility.billing_type = rateData.billing_type;
                  }

                  // Add debugging for configuration
                  console.log('Utility Configuration:', {
                    billing_type: existingUtility.billing_type,
                    net_metering_specs: existingUtility.net_metering_specs,
                    net_billing_specs: existingUtility.net_billing_specs
                  });

                } catch (error) {
                  console.error('Error fetching company rate:', error);
                }
              }

              // Replace the utility object with our updated version
              Object.assign(utility, existingUtility);
            }
            // console.log(data);
            return data;
          }),
        optionalBootstrapRequest(newEndpoint('crm/sources').list(), [], 'sources'),
        optionalBootstrapRequest(newEndpoint('crm/lead_status').list(), [], 'statuses'),
        optionalBootstrapRequest(newEndpoint('crm/lead_reason_for_loss').list(), [], 'loss reasons'),
        optionalBootstrapRequest(
          newEndpoint('hardware/roofs').list()
            .then((data) => data.map(({ cost, ...roof }) => roof)),
          [],
          'roof types',
        ),
      ]).then(([leadData, sources, statuses, lossReasons, roofTypes]) => ({
        ...leadData, sources, statuses, lossReasons, roofTypes,
      }));
    }

    if (lead === undefined && uid !== 'new' && uid !== undefined) {
      console.log('Fetching lead data.');
      setLeadError(null);
      fetchData().then((data) => {
        const homeowner = data.homeowners?.[0];
        const title = homeowner?.first_name || homeowner?.last_name
          ? `${homeowner.first_name || ''} ${homeowner.last_name || ''}`.trim()
          : data.address?.street || data.property?.street || 'Lead';
        setTitle(title);
        setLead(data);

        // Re-hydrate stepsDone from the server lead so users returning to an
        // existing lead can continue the flow (e.g. see the Confirm Home
        // section). Merge instead of replace to preserve any in-session steps
        // like 'Manual Lead' that were just set during creation.
        setStepsDone((prev) => {
          const next = new Set(prev);
          next.add('Customer Details');
          // A persisted lead has, at minimum, the Customer Details step done.
          // Property Details unlocks the PropertyDetails section (map +
          // Confirm Home in Phase A, the property form in Phase B).
          next.add('Property Details');
          // Utility Details unlocks once the property has a chosen image
          // source or AI imagery (i.e. user has confirmed home / run AI).
          if (data?.property?.design_type || data?.property?.rgb) {
            next.add('Utility Details');
          }
          return Array.from(next);
        });
      }).catch((e) => {
        console.log(e);
        setLeadError('Lead not found.');
        setLead(null);
        // if (!iframe) history.push('/404');
      });
    }
  }, [uid, lead]);

  useEffect(() => setTitle('New Lead'), []);

  return (
    <LeadContext.Provider
      value={{
        user,
        company,
        uid,
        tab,
        lead,
        AiDesigns,
        setUtility,
        validUtility,
        newEndpoint,
        displaySnack,
        navigateTab,
        leadAPI: API,
        updateLead,
        refreshLead,
        noteAPI,
        taskAPI,
        documentAPI,
        proposalAPI,
        CanvasAPI,
        hardwareAPI,
        packageAPI,
        productionMapAPI,
        saveShadingsAPI,
        removeShadingsAPI,
        runShadingsAPI,
        runShadingRequiredClientDataAPI,
        leadImagesAPI,
        thumbnailsAPI,
        exportDesignAPI,
        fieldsAPI,
        coverageAPI,
        namevaluesAPI,
        pinHouse,
        predictAPI,
        leadError,
        PVWattsAPI,
        PVWattsHourlyAPI,
        StringingAPI,
        projectAPI,
        paymentAPI,
        measureAPI,
        optimizeAPI,
        edgeDetectionAPI,
        panelPlacementAPI,
        getTaskStatus,
        stepsDone,
        stepsDoneHandler,
        canvasImportDxfAPI,
        // Solar Production Service APIs
        solarProductionDesignAPI,
        solarProductionOptimalAPI,
        solarProductionSimulateAPI,
      }}
    >
      {children}
    </LeadContext.Provider>
  );
};

export default LeadProvider;
